import React, { useState, useEffect } from 'react';

import { getResume, saveResume, downloadResumePdf } from './restApi';
import {
  DocumentManager,
  setCursorPositionInDOM,
  handleSelectionChange,
  getDescendantNodeAndOffset,
} from '../../RichTextEditor';

import ResumeRichTextEditor from './ResumeRichTextEditor';
import ResumeFormattingControls from './ResumeFormattingControls';
import Feedback from '../../Shared/UI/Feedback/components/Feedback';
import Button from '../../Shared/UI/Button/components/Button';
import ActionRow from '../../Shared/UI/ActionRow/components/ActionRow';

import styles from './Resume.module.scss';

const Resume = ({ authenticatedUser, id }) => {
  const [state, setState] = useState({
    documentManager: new DocumentManager({ startDocumentComponentType: 'DocumentParagraph' }),
    loading: true,
    feedbackMessage: null,
    feedbackVariant: null,
    resumeId: id,
    jobProspectId: null,
  });
  const [contentEditable, setContentEditable] = useState(false);

  const clearFeedback = () => {
    setState((prevState) => ({
      ...prevState,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  };

  const onResponse = (response) => {
    setState(prevState => {
      const nextState = {
        ...prevState,
        loading: false,
        feedbackMessage: response.feedbackMessage || prevState.feedbackMessage,
        feedbackVariant: response.feedbackVariant || prevState.feedbackVariant,
        jobProspectId: response.resume?.jobProspectId || prevState.jobProspectId,
      };

      if (response.resume?.latestVersion) {
        nextState.documentManager.addDocumentToHistoryFromData({
          document: {
            documentComponents: response.resume.latestVersion.documentComponents,
          },
        });
      }

      return nextState;
    });
  };

  const onError = feedback => setState(prevState => ({ ...prevState, ...feedback, loading: false }));

  useEffect(() => {
    if (id) {
      getResume({ resumeId: id, onResponse, onError });
    }
  }, []);

  useEffect(() => {
    setCursorPositionInDOM({
      editableContentId: styles.resumeEditorContainer,
      isibuteDocument: state.documentManager.getCurrentDocument(),
    });
  }, [state.documentManager.historyIndex]);

  useEffect(() => {
    if (contentEditable) {
      const editableElement = document.getElementById(styles.resumeEditorContainer);
      if (editableElement) {
        editableElement.focus();

        const { descendantNode, offset } = getDescendantNodeAndOffset({
          node: editableElement,
          targetPosition: 1,
        });

        const selection = window.getSelection();
        const range = document.createRange();
        range.setStart(descendantNode, offset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        handleSelectionChange({ editableContentId: styles.resumeEditorContainer, setState });
      }
    }
  }, [contentEditable]);

  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

  const handleSave = () => {
    setContentEditable(false);
    saveResume({
      resumeId: id,
      onResponse,
      onError,
      body: {
        ...state.documentManager.getCurrentDocument().asJSON(),
        authenticityToken,
      },
    });
  };

  const handleDownloadPdf = () => {
    downloadResumePdf({ resumeId: id, onError });
  };

  if (state.loading) {
    return <div className={styles.loading}>Loading resume...</div>;
  }

  const fromParam = new URLSearchParams(window.location.search).get('from');
  const backUrl = fromParam || '/job_prospects';
  const backLabel = fromParam?.startsWith('/groups/')
    ? 'Back to Group Job Prospects'
    : 'Back to Job Prospects';

  return (
    <>
      <div className={styles.controlsSection}>
        <ActionRow>
          <a href={backUrl} className={styles.backLink}>← {backLabel}</a>
        </ActionRow>
        <ActionRow>
          {contentEditable ? (
            <>
              <Button variant="primary" onClick={handleSave}>Save</Button>
              <Button variant="secondary" onClick={() => setContentEditable(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button variant="primary" onClick={() => setContentEditable(true)}>Edit</Button>
              <Button variant="secondary" onClick={handleDownloadPdf}>Download PDF</Button>
            </>
          )}
        </ActionRow>
      </div>
      <ResumeFormattingControls
        contentEditable={contentEditable}
        setState={setState}
        activeStyleTypes={state.activeStyleTypes}
        activeFormat={state.activeFormat}
      />
      <ResumeRichTextEditor
        setState={setState}
        state={state}
        contentEditable={contentEditable}
      />
      <Feedback
        message={state?.feedbackMessage}
        variant={state?.feedbackVariant}
        closeFeedback={clearFeedback}
      />
    </>
  );
};

export default Resume;
